/**
 * MediaCard / MediaThumbnail — a single Media Centre grid/list card.
 *
 * Extracted from MediaCentre.js. The thumbnail selection (previously a deeply
 * nested ternary) is flattened here into ordered early-returns. Behaviour is
 * intentionally identical to the original inline render.
 */
import React from 'react';
import {
    getMediaTypeIcon,
    getMediaTypeDisplay,
    getThemeColor,
    getProductColor,
    showDuration,
    showPlayOverlay,
    isBriefing,
} from './helpers';

const DEFAULT_THUMB = '/assets/img/media/default-thumb.jpg';

const isAudio = (item) => item.mediaType.toLowerCase() === 'audio' || item.mediaType === 'Audio';

/** Hide the broken <img> and reveal its sibling .thumbnail-placeholder. */
function showPlaceholderOnError(e) {
    e.target.style.display = 'none';
    const placeholder = e.target.parentNode.querySelector('.thumbnail-placeholder');
    if (placeholder) {
        placeholder.style.display = 'flex';
    }
}

/**
 * Picks the right thumbnail representation for an item. Ordered branches mirror
 * the original ternary exactly, with Briefing added ahead of them (it has no
 * media file, so none of the URL-driven branches apply):
 *   0. briefing → audio-briefing placeholder
 *   1. a fetched/custom thumbnail (videos + audio)
 *   2. local video (no external platform link) → icon placeholder
 *   3. audio → audio placeholder
 *   4. image → the image itself (or a loading placeholder)
 *   5. external video whose thumbnail is still loading → spinner
 *   6. fallback → manual/vimeo thumbnail or the default image
 */
function MediaThumbnail({ item, fetchedThumbnail, imageUrl, areThumbnailsLoading }) {
    const type = item.mediaType.toLowerCase();
    const link = item.webURLLink || '';
    const isExternalVideo =
        link.includes('vimeo.com') || link.includes('youtube.com') || link.includes('youtu.be');

    // 0. Briefing — no media file to derive a thumbnail from
    if (isBriefing(item.mediaType)) {
        return (
            <div className="thumbnail-placeholder local-audio-thumb briefing-thumb">
                <div className="audio-icon-wrapper">
                    <i className="fas fa-headphones" style={{ fontSize: '2rem' }}></i>
                </div>
                <div className="audio-title">{item.topic}</div>
            </div>
        );
    }

    // 1. Fetched thumbnail (external or custom blob thumbnail, for videos and audio)
    if (fetchedThumbnail) {
        return (
            <>
                <img src={fetchedThumbnail} alt={item.topic} onError={showPlaceholderOnError} />
                <div className="thumbnail-placeholder" style={{ display: 'none' }}>
                    <i className={getMediaTypeIcon(item.mediaType)}></i>
                    <span>{getMediaTypeDisplay(item.mediaType)}</span>
                </div>
            </>
        );
    }

    // 2. Local video without a custom thumbnail
    if (type === 'video' && !isExternalVideo) {
        return (
            <div className="thumbnail-placeholder local-video-thumb">
                <div className="video-icon-wrapper">
                    <i className="fas fa-play-circle"></i>
                </div>
                <div className="video-title-overlay">
                    <span>{item.topic}</span>
                </div>
            </div>
        );
    }

    // 3. Audio without a thumbnail
    if (isAudio(item)) {
        return (
            <div className="thumbnail-placeholder local-audio-thumb">
                <div className="audio-icon-wrapper">
                    <i className="icon-volume-2" style={{ fontSize: '2rem' }}></i>
                </div>
                <div className="audio-title">{item.topic}</div>
            </div>
        );
    }

    // 4. Image media type
    if (type === 'image') {
        return (
            <>
                {imageUrl ? (
                    <>
                        <img
                            src={imageUrl}
                            alt={item.topic}
                            className="image-thumbnail"
                            onError={showPlaceholderOnError}
                        />
                        <div className="image-overlay">
                            <i className="fas fa-expand"></i>
                        </div>
                    </>
                ) : (
                    <div className="thumbnail-placeholder image-loading">
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Loading...</span>
                    </div>
                )}
                <div className="thumbnail-placeholder image-placeholder" style={{ display: 'none' }}>
                    <i className="fas fa-image"></i>
                    <span>Image</span>
                </div>
            </>
        );
    }

    // 5. External video whose thumbnail is still being fetched
    if (areThumbnailsLoading && type === 'video' && isExternalVideo) {
        return (
            <div
                className="thumbnail-placeholder"
                style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
            >
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading thumbnail...</span>
            </div>
        );
    }

    // 6. Fallback — manual/Vimeo thumbnail, else the default image
    return (
        <>
            <img
                src={item.vimeoThumbnail || item.thumbnail || DEFAULT_THUMB}
                alt={item.topic}
                onError={(e) => {
                    // Try the Vimeo thumbnail if the manual one fails, else show the placeholder
                    if (item.vimeoThumbnail && e.target.src !== item.vimeoThumbnail) {
                        e.target.src = item.vimeoThumbnail;
                    } else {
                        showPlaceholderOnError(e);
                    }
                }}
            />
            <div className="thumbnail-placeholder" style={{ display: 'none' }}>
                <i className={getMediaTypeIcon(item.mediaType)}></i>
                <span>{getMediaTypeDisplay(item.mediaType)}</span>
            </div>
        </>
    );
}

function MediaCard({ item, fetchedThumbnail, imageUrl, areThumbnailsLoading, onMediaClick }) {
    return (
        <div
            className={`media-card media-card-${item.mediaType.toLowerCase()}`}
            onClick={(e) => onMediaClick(item, e)}
        >
            <div className="media-thumbnail">
                <MediaThumbnail
                    item={item}
                    fetchedThumbnail={fetchedThumbnail}
                    imageUrl={imageUrl}
                    areThumbnailsLoading={areThumbnailsLoading}
                />
                {showPlayOverlay(item.mediaType) && (
                    <div className="play-overlay">
                        <i className={`fas ${isAudio(item) || isBriefing(item.mediaType) ? 'fa-headphones' : 'fa-play-circle'}`}></i>
                    </div>
                )}
                {showDuration(item.mediaType) && item.duration && (
                    <div className="duration-badge">{item.duration}</div>
                )}
                <div className="media-type-badge">
                    <i className={getMediaTypeIcon(item.mediaType)}></i>
                    <span>{getMediaTypeDisplay(item.mediaType)}</span>
                </div>
            </div>

            <div className="media-info">
                <h3 className="media-title" title={item.topic}>{item.topic}</h3>
                <p className="media-description">{item.shortDescription}</p>

                <div className="media-tags">
                    {item.series && item.series.length > 0 && item.series
                        .filter((series) => series && series.trim() !== '')
                        .map((series) => (
                            <span key={series} className="tag series-tag">
                                {series}
                            </span>
                        ))}
                    {item.productService && item.productService.trim() !== '' && (
                        <span className={`tag product-tag tag-${getProductColor(item.productService)}`}>
                            {item.productService}
                        </span>
                    )}
                    {item.theme && item.theme.trim() !== '' && (
                        <span className={`tag theme-tag tag-${getThemeColor(item.theme)}`}>
                            {item.theme}
                        </span>
                    )}
                </div>

                <div className="media-meta">
                    <span className="publish-date">
                        <i className="fas fa-calendar"></i>
                        {new Date(item.publishDate).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                        })}
                    </span>
                    <span className="entity">
                        <i className="fas fa-building"></i>
                        {item.entity}
                    </span>
                </div>
            </div>
        </div>
    );
}

export default MediaCard;
